import type { AreaMetric, BarMetric, Metric } from '@vizit/molecules';
import type { OptimizationRecommendationDataMap } from '../optimization-recommendation-card';
import type { FactorStatus, ScoreFactors } from './types';

/**
 * Converts penalty flag to factor status
 * No penalty (false) = pass, has penalty (true/undefined) = alert
 */
const toStatus = (penalty: boolean | undefined): FactorStatus => (penalty === false ? 'pass' : 'alert');

/**
 * Finds a metric by key from the metrics array
 */
const findMetric = <T extends Metric>(metrics: Metric[] | undefined, key: string): T | undefined => {
  return metrics?.find((m) => m.key === key) as T | undefined;
};

/**
 * Checks if a value is within the optimal band for bar metrics
 * Includes edge case: when optimal band extends to range.max and value exceeds it,
 * treat as optimal since we can't know if higher is better without more reference data
 */
const isInOptimalBand = (currentValue: number, optimalBand: number[], range: { min: number; max: number }): boolean => {
  if (optimalBand.length === 0) return false;

  const bandMin = Math.min(...optimalBand);
  const bandMax = Math.max(...optimalBand);

  // If optimalBand is a discrete list (e.g., [2, 3, 4]), check if value is in the list
  if (optimalBand.every((v) => Number.isInteger(v))) {
    const isInList = optimalBand.includes(Math.round(currentValue));

    // Edge case: if optimal band extends to range max and value exceeds it, treat as optimal
    const tolerance = (range.max - range.min) * 0.05;
    const bandExtendsToMax = Math.abs(bandMax - range.max) < tolerance;
    const valueExceedsRange = currentValue > range.max;

    return isInList || (bandExtendsToMax && valueExceedsRange);
  }

  // If optimalBand is a range (e.g., [0.337, 0.374]), check if value is within range
  const isInRange = currentValue >= bandMin && currentValue <= bandMax;

  // Edge case: if optimal band extends to range.max and value exceeds it, treat as optimal
  const tolerance = (range.max - range.min) * 0.05;
  const bandExtendsToMax = Math.abs(bandMax - range.max) < tolerance;
  const valueExceedsRange = currentValue > range.max;

  return isInRange || (bandExtendsToMax && valueExceedsRange);
};

export function getFactorsFromRecommendationsV2(metrics: Metric[] | undefined): ScoreFactors | undefined {
  if (!metrics || metrics.length === 0) return undefined;

  const avgCarouselScore = findMetric<AreaMetric>(metrics, 'avg_carousel_score');
  const heroScore = findMetric<AreaMetric>(metrics, 'hero_score');
  const nCarouselImages = findMetric<BarMetric>(metrics, 'n_carousel_images');
  const visualDiversity = findMetric<BarMetric>(metrics, 'visual_diversity');

  return {
    carouselScore: avgCarouselScore && avgCarouselScore.currentValue >= 80 ? 'pass' : 'alert',
    heroImage: heroScore && heroScore.currentValue >= 80 ? 'pass' : 'alert',
    assetCount:
      nCarouselImages &&
      isInOptimalBand(nCarouselImages.currentValue, nCarouselImages.optimalBand, nCarouselImages.range)
        ? 'pass'
        : 'alert',
    assetMix:
      visualDiversity &&
      isInOptimalBand(visualDiversity.currentValue, visualDiversity.optimalBand, visualDiversity.range)
        ? 'pass'
        : 'alert',
  };
}

/**
 * Converts optimization recommendations V1 to ScoreExplain factor statuses
 * Uses the old penalty-based logic, with optional heroScore for hero image calculation
 */
export function getFactorsFromRecommendations(
  recommendations: OptimizationRecommendationDataMap | null | undefined,
  heroScore: number,
): ScoreFactors | undefined {
  if (!recommendations) return undefined;

  const heroImageStatus = heroScore >= 80 ? 'pass' : 'alert';

  return {
    heroImage: heroImageStatus,
    carouselScore: toStatus(recommendations.image_scores?.penalty),
    assetMix: toStatus(recommendations.image_mix?.penalty),
    assetCount: toStatus(recommendations.image_count?.penalty),
  };
}
